<template>
	<vue3-progress-bar />
	<div
		:class="[
			'app app-dashboard app-dashboard-' + effectiveTheme,
			{
				'app-dividers': appOption.appThemeDividers,
				'app-gradients': appOption.appThemeGradients,
				'app-shadows': appOption.appThemeShadows,
			},
		]"
		:data-bs-theme="effectiveTheme"
		:style="`
      --bs-primary: ${appOption.$state.appPrimaryColor};
      --bs-primary-rgb: ${hexToRgbList(appOption.$state.appPrimaryColor)};
      --bs-secondary: ${appOption.$state.appSecondaryColor};
      --bs-secondary-rgb: ${hexToRgbList(appOption.$state.appSecondaryColor)};
    `"
	>
		<router-view />
	</div>
	<toast-wrapper />
	<div :class="{ 'app-staging': isStaging }"></div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import { ProgressFinisher, useProgress } from '@marcoschulte/vue3-progress';

import { useAppOptionsStore, hexToRgbList } from '@lib/stores/app-options';
import { useColorScheme } from '@lib/composables/useColorScheme';
import ToastWrapper from '@lib/plugins/toasts/ToastWrapper.vue';

import '@lib/plugins/bootstrap';

/*
 * States
 */
const router = useRouter();

const appOption = useAppOptionsStore();
const { scheme } = useColorScheme();

const effectiveTheme = computed(() => (appOption.appTheme === 'auto' ? scheme.value : appOption.appTheme));

const progresses = [] as ProgressFinisher[];
const isStaging = globalThis.env?.app?.env != 'production';

/*
 * Callbacks
 */

// Register interaction with the process bar
router.beforeEach(async () => {
	progresses.push(useProgress().start());
	document.body.scrollTop = 0;
	document.documentElement.scrollTop = 0;
});

router.afterEach(async () => {
	progresses.pop()?.finish();
});

axios.interceptors.request.use((config) => {
	progresses.push(useProgress().start());
	return config;
});

axios.interceptors.response.use(
	(resp) => {
		progresses.pop()?.finish();
		return resp;
	},
	(error) => {
		progresses.pop()?.finish();
		return Promise.reject(error);
	},
);

// Cleanup leftover backdrops if a RouterLink is clicked inside a model
router.afterEach(() => {
	document.body.classList.remove('modal-open');
	document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
});
</script>

<style lang="scss">
$vue3-progress-bar-color: rgb(var(--bs-primary-rgb));
@import '@marcoschulte/vue3-progress/dist/';

.app-staging {
	--spacing: 2px;
	--color: var(--bs-red);
	--color-bg: var(--bs-light);

	&:before {
		content: ' '; /* to ensure it displays */
		position: absolute;
		left: var(--spacing);
		right: var(--spacing);
		top: var(--spacing);
		bottom: var(--spacing);
		border: var(--spacing) dashed var(--color);
		border-radius: var(--bs-border-radius);
		pointer-events: none; /* user can't click on it */
		z-index: 1090;
	}

	&::after {
		content: 'Staging - Do not use in production'; /* to ensure it displays */
		position: absolute;
		color: var(--color);
		background-color: var(--color-bg);
		padding: 0.25rem 0.5rem;
		bottom: calc(3 * var(--spacing));
		pointer-events: none; /* user can't click on it */
		left: 50%;
		transform: translate(-50%, 0);
		z-index: 1090;
		white-space: nowrap !important;
		text-transform: uppercase;
		font-family: 'Courier';
		font-weight: bolder;
		border: 5px solid var(--color);
		border-radius: 0.25rem;
	}
}

// Add improvement for bitwarden var
#bit-notification-bar-spacer {
	position: fixed;
}
</style>
